import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import "./ApprovalAdmin.css";

const ADMIN_HEADERS = {
  "x-admin-username": "Alumini2026",
  "x-admin-password": "Alu26@"
};

const PAYMENT_BASE_LINK = "https://your-college-portal.com/alumni/payment";

const ApprovedAlumni = () => {
  const [approvedAlumni, setApprovedAlumni] = useState([]);
  const [loadingId, setLoadingId] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const getRequestConfig = () => ({
    headers: ADMIN_HEADERS
  });

  useEffect(() => {
    loadApprovedAlumni();
  }, []);

  const loadApprovedAlumni = async () => {
    try {
      setErrorMessage("");
      const res = await api.get("/api/admin/approved", getRequestConfig());
      setApprovedAlumni(Array.isArray(res.data?.approvedAlumni) ? res.data.approvedAlumni : []);
    } catch (error) {
      console.error("Load approved alumni error:", error);
      setErrorMessage(error?.response?.data?.message || "Failed to load approved alumni");
    }
  };

  const getRecordId = (person) => person?._id || person?.id || "";

  const normalizePhoneNumber = (phone) => {
    if (!phone) return "";
    let cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length === 10) cleaned = `91${cleaned}`;
    return cleaned;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB");
  };

  const openWhatsAppMessage = (phone, message) => {
    if (!phone) return;
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const getPaymentLinkMessage = (alumni) => {
    const paymentLink = `${PAYMENT_BASE_LINK}?alumniId=${encodeURIComponent(alumni.alumniId || alumni._id)}`;

    return `
Alumni Membership Payment Link

Dear ${alumni.name},

Greetings from the Alumni Association.

Your alumni membership application has been approved successfully.

Please use the official payment link below to complete your membership fee payment:

Alumni ID: ${alumni.alumniId}
Course: ${alumni.course}
Batch: ${alumni.batchYear}

Official Payment Link:
${paymentLink}

Once payment is completed and confirmed, your alumni membership benefits will be activated.

Regards,
Alumni Relations Team
CIT Coimbatore
`.trim();
  };

  const sendPaymentLink = async (alumni) => {
    const recordId = getRecordId(alumni);
    if (!recordId) return;

    try {
      setLoadingId(recordId);
      setSuccessMessage("");
      setErrorMessage("");

      const res = await api.patch(
        `/api/admin/approved/${recordId}/payment-link-sent`,
        {},
        getRequestConfig()
      );

      const updatedAlumni = res.data?.alumni || alumni;

      const phone = normalizePhoneNumber(updatedAlumni.mobile);
      if (phone) {
        const message = getPaymentLinkMessage(updatedAlumni);
        openWhatsAppMessage(phone, message);
      }

      setApprovedAlumni((prev) =>
        prev.map((item) =>
          getRecordId(item) === getRecordId(updatedAlumni) ? updatedAlumni : item
        )
      );

      setSuccessMessage(`✅ Payment link sent to ${updatedAlumni.name}`);
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Send payment link error:", error);
      setErrorMessage(error?.response?.data?.message || "Failed to send payment link");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setLoadingId("");
    }
  };

  const getPaymentStatusBadge = (alumni) => {
    if (alumni.paymentLinkSent) {
      return <span className="payment-status-badge sent">Link Sent</span>;
    }
    return <span className="payment-status-badge pending">Pending</span>;
  };

  return (
    <div className="approval-admin">
      <div className="dashboard-header">
        <div>
          <h1>Approved Alumni Directory</h1>
          <p>Manage approved alumni records and share official membership payment links.</p>
        </div>

        <div className="header-actions">
          <Link to="/admin/approval" className="btn btn-secondary">
            Back to Approval Queue
          </Link>
          <button onClick={loadApprovedAlumni} className="btn btn-outline">
            🔄 Refresh
          </button>
        </div>
      </div>

      {successMessage && <div className="success-banner">{successMessage}</div>}
      {errorMessage && <div className="error-banner">{errorMessage}</div>}

      <div className="table-container">
        <table className="alumni-table">
          <thead>
            <tr>
              <th>Alumni ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Course</th>
              <th>Batch</th>
              <th>Approval Date</th>
              <th>Payment Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {approvedAlumni.length === 0 ? (
              <tr>
                <td colSpan={9} className="no-data">
                  No approved alumni found
                </td>
              </tr>
            ) : (
              approvedAlumni.map((person) => {
                const recordId = getRecordId(person);

                return (
                  <tr key={recordId}>
                    <td><strong>{person.alumniId || "-"}</strong></td>
                    <td>{person.name}</td>
                    <td>{person.email}</td>
                    <td>{person.mobile}</td>
                    <td>{person.course}</td>
                    <td>{person.batchYear}</td>
                    <td>{formatDate(person.approvalDate)}</td>
                    <td>{getPaymentStatusBadge(person)}</td>
                    <td>
                      {person.paymentLinkSent ? (
                        <button className="btn btn-success btn-sm" disabled>
                          ✓ Sent
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => sendPaymentLink(person)}
                          disabled={loadingId === recordId}
                        >
                          {loadingId === recordId ? "Sending..." : "Send Payment Link"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApprovedAlumni;